import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { CacheKey } from '../../../types';
import { coreClient } from '../../../utils/request';
import { actions, getPickupSituation } from '../ducks';
import { ContactCreateParams } from '@brenger/api-client';

interface UseStopContactPrefill {
  isLoading: boolean;
}

/**
 * Hook that takes care of prefilling the correct info
 */

export const useStopContactDetailsPrefill = (stopType: 'pickup' | 'delivery'): UseStopContactPrefill => {
  const destination = useTypedSelector(state => state.generalTransport.destination);
  const accountSlug = destination[stopType === 'pickup' ? 'pickup_meta' : 'delivery_meta']?.utm_account;
  const existingData = useTypedSelector(state => state.generalTransport.contact[stopType]?.contact_details);
  const pickupSituation = useTypedSelector(getPickupSituation);
  const dispatch = useDispatch();

  const publicAccount = useQuery({
    queryKey: [CacheKey.RETRIEVE_PUBLIC_DATA, stopType, accountSlug],
    queryFn: () => coreClient.accounts.retrievePublicDataBySlug({ slug: accountSlug as string }),
    enabled: !!accountSlug,
  });

  React.useEffect(() => {
    // Skip when pickup is auction
    if (pickupSituation === 'auction' && stopType === 'pickup') return;
    /**
     * In case of auction and pickup, we always want to prefill the pickup contact details with default values
     * In the other cases we check for existing public account data
     */

    const details = publicAccount.data;
    const isDetailsValid = details && typeof details === 'object' && details.email;
    const shouldPrefill = !existingData && isDetailsValid;
    if (shouldPrefill) {
      dispatch(
        actions.setContactDetails({
          contact_type: stopType,
          details: details as ContactCreateParams,
        })
      );
      return;
    }
  }, [pickupSituation, publicAccount.data]);

  return {
    isLoading: publicAccount.isLoading,
  };
};
